/**
 * Configuration des aides proposees a l'eleve.
 *
 * Chaque liste pilote directement l'affichage du bouton correspondant dans la
 * vue eleve : une liste vide masque le bouton. C'est ce qui permet a l'aide aux
 * selecteurs de n'apparaitre que lorsque l'enseignant la prevoit, au lieu
 * d'imposer un contenu fige a toutes les lecons.
 */

import React, { useState } from 'react';
import { Button, Card, Empty, Input, List, Modal, Space, Tabs, message } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { HtmlCssConfig } from '../../models/htmlcss/types';
import { HtmlCssHelpItem } from './core/types';
import { CSS_HELP_ITEMS, HTML_HELP_ITEMS, SELECTOR_HELP_ITEMS } from './assets/help';

type HelpKind = 'html' | 'css' | 'selector';

interface HtmlCssHelpConfigPanelProps {
  initialConfig: HtmlCssConfig;
  onSave?: (config: HtmlCssConfig) => void;
}

/** Valeurs proposees lorsqu'une liste est vide. */
const SUGGESTIONS: Record<HelpKind, HtmlCssHelpItem[]> = {
  html: HTML_HELP_ITEMS,
  css: CSS_HELP_ITEMS,
  selector: SELECTOR_HELP_ITEMS,
};

const LABELS: Record<HelpKind, string> = {
  html: 'Exemples HTML',
  css: 'Exemples CSS',
  selector: 'Sélecteurs CSS',
};

const HtmlCssHelpConfigPanel: React.FC<HtmlCssHelpConfigPanelProps> = ({
  initialConfig,
  onSave,
}) => {
  const [lists, setLists] = useState<Record<HelpKind, HtmlCssHelpItem[]>>({
    html: initialConfig.config.htmlHelpItems ?? [],
    css: initialConfig.config.cssHelpItems ?? [],
    selector: initialConfig.config.selectorHelpItems ?? [],
  });

  const [editing, setEditing] = useState<{ kind: HelpKind; index: number } | null>(null);
  const [label, setLabel] = useState('');
  const [code, setCode] = useState('');

  const openEditor = (kind: HelpKind, index: number) => {
    const item = index >= 0 ? lists[kind][index] : { label: '', code: '' };
    setEditing({ kind, index });
    setLabel(item.label);
    setCode(item.code);
  };

  const closeEditor = () => {
    setEditing(null);
    setLabel('');
    setCode('');
  };

  const commitItem = () => {
    if (!label.trim() || !code.trim()) {
      message.error('Le libellé et le code sont requis.');
      return;
    }
    if (!editing) return;

    const item: HtmlCssHelpItem = { label: label.trim(), code };
    setLists(current => {
      const list = [...current[editing.kind]];
      if (editing.index < 0) list.push(item);
      else list[editing.index] = item;
      return { ...current, [editing.kind]: list };
    });
    closeEditor();
  };

  const removeItem = (kind: HelpKind, index: number) => {
    setLists(current => ({
      ...current,
      [kind]: current[kind].filter((_, i) => i !== index),
    }));
  };

  /** Recharge les valeurs proposees, sans ecraser un travail en cours. */
  const loadSuggestions = (kind: HelpKind) => {
    setLists(current => ({ ...current, [kind]: [...SUGGESTIONS[kind]] }));
  };

  const handleSave = () => {
    onSave?.({
      ...initialConfig,
      config: {
        ...initialConfig.config,
        htmlHelpItems: lists.html,
        cssHelpItems: lists.css,
        // Une liste vide est enregistree comme absente : le bouton
        // correspondant ne sera pas affiche a l'eleve.
        selectorHelpItems: lists.selector.length > 0 ? lists.selector : undefined,
      },
    });
    message.success('Aide enregistrée.');
  };

  const renderList = (kind: HelpKind) => (
    <div className="htmlcss-config-tabpane">
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<PlusOutlined />} type="dashed" onClick={() => openEditor(kind, -1)}>
          Ajouter
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => loadSuggestions(kind)}>
          Charger les exemples proposés
        </Button>
      </Space>

      <div className="htmlcss-config-scroll">
      {lists[kind].length === 0 ? (
        <Empty
          description={
            kind === 'selector'
              ? "Aucun sélecteur configuré : le bouton d'aide ne sera pas proposé à l'élève."
              : "Aucun exemple : le bouton d'aide ne sera pas proposé à l'élève."
          }
        />
      ) : (
        <List
          dataSource={lists[kind]}
          renderItem={(item, index) => (
            <List.Item
              actions={[
                <Button
                  key="edit"
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => openEditor(kind, index)}
                  aria-label={`Modifier ${item.label}`}
                />,
                <Button
                  key="delete"
                  icon={<DeleteOutlined />}
                  size="small"
                  danger
                  onClick={() => removeItem(kind, index)}
                  aria-label={`Supprimer ${item.label}`}
                />,
              ]}
            >
              <List.Item.Meta
                title={item.label}
                description={<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{item.code}</pre>}
              />
            </List.Item>
          )}
        />
      )}
      </div>
    </div>
  );

  return (
    // Seule la liste d'exemples defile : le bouton d'enregistrement reste
    // visible meme quand la liste est longue.
    <Card
      title="Aide HTML/CSS de la leçon"
      variant="borderless"
      className="htmlcss-config-panel"
      styles={{ body: { display: 'flex', flexDirection: 'column', minHeight: 0 } }}
    >
      <Tabs
        className="htmlcss-config-tabs"
        items={(['html', 'css', 'selector'] as HelpKind[]).map(kind => ({
          key: kind,
          label: `${LABELS[kind]} (${lists[kind].length})`,
          children: renderList(kind),
        }))}
      />

      <div className="htmlcss-config-footer">
        <Button type="primary" onClick={handleSave}>
          Enregistrer l'aide
        </Button>
      </div>

      <Modal
        open={editing !== null}
        onCancel={closeEditor}
        onOk={commitItem}
        title={editing && editing.index >= 0 ? 'Modifier un exemple' : 'Ajouter un exemple'}
      >
        <Input
          placeholder="Libellé affiché à l'élève"
          value={label}
          onChange={event => setLabel(event.target.value)}
          style={{ marginBottom: 8 }}
        />
        <Input.TextArea
          placeholder="Code inséré dans l'éditeur"
          value={code}
          onChange={event => setCode(event.target.value)}
          rows={4}
        />
      </Modal>
    </Card>
  );
};

export default HtmlCssHelpConfigPanel;
